import { useTranslation } from 'react-i18next';
import { Check, Mic, Play, Plus, Search, Volume2 } from 'lucide-react';

import alexaPhoto from '../../../assets/landing/alexa-home-training.jpg';
import { TOUR_EXERCISES } from './slides';

const Thumb = ({ exercise }) => (
  <span className="mt-tour-art__thumb">
    <img src={exercise.src} alt="" />
  </span>
);

// The progress-ring language (rest timer, macros): a green arc from 12 o'clock over the track
const Ring = ({ value, className = '', children }) => (
  <span className={`mt-tour-art__ring ${className}`}>
    <svg viewBox="0 0 36 36">
      <circle className="mt-tour-art__ring-track" cx="18" cy="18" r="15.5" pathLength="100" />
      <circle className="mt-tour-art__ring-arc" cx="18" cy="18" r="15.5" pathLength="100" strokeDasharray={`${value} 100`} />
    </svg>
    <span className="mt-tour-art__ring-label">{children}</span>
  </span>
);

/**
 * Each tour step's picture, made of the app's own pieces (today card, library rows, set table, rings) and real
 * library exercises. Sized in em: the stage sets the font size from its own size (container units), so one
 * drawing serves the desktop panel and the phone's top half. Decorative: the step's copy says it all
 */
export const WelcomeArt = ({ art }) => {
  const { t } = useTranslation();
  const isPt = t('lang') === 'pt';
  const a = (key, options) => t(`components.WelcomeTour.art.${key}`, options);
  const list = (key) => {
    const value = t(`components.WelcomeTour.art.${key}`, { returnObjects: true });
    return Array.isArray(value) ? value : [];
  };
  const name = (key) => (isPt ? TOUR_EXERCISES[key].pt : TOUR_EXERCISES[key].en);
  const number = new Intl.NumberFormat(isPt ? 'pt-BR' : 'en-US');

  if (art === 'today') {
    return (
      <div className="mt-tour-art mt-tour-art--today" aria-hidden="true">
        <div className="mt-tour-art__card mt-tour-art__today">
          <p className="mt-tour-art__today-title">
            {a('workoutA')} <span className="mt-tour-art__pill">{a('today')}</span>
          </p>
          <p className="mt-tour-art__muscles">{a('muscles')}</p>
          <p className="mt-tour-art__meta">{a('todayMeta')}</p>
          <span className="mt-tour-art__thumbs">
            {['bench', 'row', 'curl', 'raise'].map((key) => (
              <Thumb key={key} exercise={TOUR_EXERCISES[key]} />
            ))}
          </span>
          <span className="mt-tour-art__start">
            <Play aria-hidden="true" />
            {a('startWorkout')}
          </span>
        </div>
      </div>
    );
  }

  if (art === 'build') {
    const muscles = list('libraryMuscles');
    const chips = list('aiChips');
    return (
      <div className="mt-tour-art mt-tour-art--build" aria-hidden="true">
        <div className="mt-tour-art__card mt-tour-art__library">
          <span className="mt-tour-art__search">
            <Search />
            {a('search')}
          </span>
          {['bench', 'row', 'curl'].map((key, index) => (
            <span key={key} className="mt-tour-art__lib-row">
              <Thumb exercise={TOUR_EXERCISES[key]} />
              <span className="mt-tour-art__lib-text">
                <span className="mt-tour-art__lib-name">{name(key)}</span>
                <span className="mt-tour-art__lib-sub">{muscles[index]}</span>
              </span>
              <span className="mt-tour-art__add">
                <Plus />
              </span>
            </span>
          ))}
        </div>
        <div className="mt-tour-art__card mt-tour-art__ai">
          <p className="mt-tour-art__ai-title">
            {a('aiTitle')} <span className="mt-tour-art__pro">PRO</span>
          </p>
          <span className="mt-tour-art__chips">
            {chips.map((chip, index) => (
              <span key={chip} className={`mt-tour-art__chip${index < 3 ? ' mt-tour-art__chip--on' : ''}`}>
                {index < 3 && <Check />}
                {chip}
              </span>
            ))}
          </span>
          <span className="mt-tour-art__generate">
            {a('generate')}
          </span>
        </div>
      </div>
    );
  }

  if (art === 'guided') {
    const sets = [
      { n: 1, reps: 12, kg: 40, done: true },
      { n: 2, reps: 10, kg: 45, now: true },
      { n: 3, reps: 10, kg: 45 },
    ];
    return (
      <div className="mt-tour-art mt-tour-art--guided" aria-hidden="true">
        <div className="mt-tour-art__card mt-tour-art__session">
          <span className="mt-tour-art__media">
            <img src={TOUR_EXERCISES.bench.src} alt="" />
          </span>
          <p className="mt-tour-art__session-name">{name('bench')}</p>
          <p className="mt-tour-art__meta">{a('setOf')}</p>
          <span className="mt-tour-art__sets">
            <span className="mt-tour-art__set mt-tour-art__set--head">
              <span>{a('set')}</span>
              <span>{a('reps')}</span>
              <span>{a('load')}</span>
              <span />
            </span>
            {sets.map(({ n, reps, kg, done, now }) => (
              <span key={n} className={`mt-tour-art__set${now ? ' mt-tour-art__set--now' : ''}`}>
                <span>{n}</span>
                <span>{reps}</span>
                <span>{kg} kg</span>
                <span className={`mt-tour-art__check${done ? ' mt-tour-art__check--done' : ''}`}>{done && <Check />}</span>
              </span>
            ))}
          </span>
        </div>
        <div className="mt-tour-art__card mt-tour-art__voice">
          <Volume2 />
          {a('voiceLine')}
        </div>
        <div className="mt-tour-art__card mt-tour-art__rest">
          <Ring value={70}>
            <strong>0:45</strong>
            <small>{a('rest')}</small>
          </Ring>
        </div>
      </div>
    );
  }

  if (art === 'progress') {
    const days = list('weekDays');
    const bars = [0, 62, 0, 78, 0, 88, 0];
    return (
      <div className="mt-tour-art mt-tour-art--progress" aria-hidden="true">
        <div className="mt-tour-art__card mt-tour-art__week">
          <p className="mt-tour-art__week-title">
            {a('thisWeek')} <span className="mt-tour-art__pill">{a('weekCount')}</span>
          </p>
          <span className="mt-tour-art__bars">
            {bars.map((height, index) => (
              <span key={days[index] ? `${days[index]}${index}` : index} className="mt-tour-art__bar">
                <span
                  className={`mt-tour-art__bar-fill${height ? '' : ' mt-tour-art__bar-fill--off'}${index === 5 ? ' mt-tour-art__bar-fill--now' : ''}`}
                  style={{ height: `${height || 6}%` }}
                />
                <span className="mt-tour-art__bar-day">{days[index]}</span>
              </span>
            ))}
          </span>
        </div>
        <span className="mt-tour-art__stats">
          <span className="mt-tour-art__card mt-tour-art__stat">
            <strong>5</strong>
            <small>{a('streak')}</small>
          </span>
          <span className="mt-tour-art__card mt-tour-art__stat">
            <strong>{number.format(12450)}</strong>
            <small>{a('volume')}</small>
          </span>
        </span>
      </div>
    );
  }

  if (art === 'alexa') {
    return (
      <div className="mt-tour-art mt-tour-art--alexa" aria-hidden="true">
        <span className="mt-tour-art__photo">
          <img src={alexaPhoto} alt="" />
        </span>
        <div className="mt-tour-art__card mt-tour-art__say">
          <Mic />
          {a('alexaPhrase')}
        </div>
        <div className="mt-tour-art__card mt-tour-art__reply">{a('alexaReply')}</div>
      </div>
    );
  }

  if (art === 'diet') {
    const meals = list('meals');
    const macros = [
      { label: list('macros')[0], value: 30 },
      { label: list('macros')[1], value: 45 },
      { label: list('macros')[2], value: 25 },
    ];
    return (
      <div className="mt-tour-art mt-tour-art--diet" aria-hidden="true">
        <div className="mt-tour-art__card mt-tour-art__meals">
          <p className="mt-tour-art__week-title">
            {a('dietTotal')} <span className="mt-tour-art__pill">{a('dietGoal')}</span>
          </p>
          {meals.map((meal) => (
            <span key={meal.name} className="mt-tour-art__meal">
              <span className="mt-tour-art__lib-text">
                <span className="mt-tour-art__lib-name">{meal.name}</span>
                <span className="mt-tour-art__lib-sub">{meal.foods}</span>
              </span>
              <strong>{meal.kcal}</strong>
            </span>
          ))}
        </div>
        <div className="mt-tour-art__card mt-tour-art__macros">
          {macros.map(({ label, value }) => (
            <span key={label || value} className="mt-tour-art__macro">
              <Ring value={value}>
                <strong>{value}%</strong>
              </Ring>
              <small>{label}</small>
            </span>
          ))}
        </div>
      </div>
    );
  }

  // The last step: the editor's head for the first workout, under the brand mark
  return (
    <div className="mt-tour-art mt-tour-art--start" aria-hidden="true">
      <img className="mt-tour-art__logo" src="/meuTreinoLogoClean.png" alt="" />
      <div className="mt-tour-art__card mt-tour-art__first">
        <span className="mt-tour-art__letters">
          {'ABCDE'.split('').map((letter, index) => (
            <span key={letter} className={index === 0 ? 'mt-tour-art__letter--on' : undefined}>
              {letter}
            </span>
          ))}
        </span>
        <p className="mt-tour-art__today-title">{a('workoutA')}</p>
        <span className="mt-tour-art__start">
          <Plus aria-hidden="true" />
          {a('createWorkout')}
        </span>
      </div>
    </div>
  );
};
